import { useEffect, useMemo, useState } from 'react';

import {
  App,
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Steps,
  DatePicker,
  Segmented,
  Tooltip,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { Database, PlayCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { usePostGeoSarAnalyzeMutation } from '@/features/analytics/api/geoProcessingAnalysisApi';
import { useFloodJobs } from '@/features/analytics/context/FloodJobsContext';
import { mapSarAnalyzeResponseToFloodJob } from '@/features/analytics/utils/mapSarAnalyzeResponseToFloodJob';
import { useAppSelector } from '@/hooks/useAppSelector';
import {
  DEFAULT_STAGE_WEIGHTS,
  DEFAULT_TARGET_DURATION_MS,
  type FloodJob,
  type SarAoiAnalysisKind,
} from '@/mocks/floodJobs';
import { formatUserFacingApiError } from '@/utils/getApiErrorMessage';

import { getAnalysisKindMeta } from '../../utils/analysisKindMeta';
import {
  computeAoiAreaKm2,
  getSarAoiMaxAreaKm2,
  openLngLatRingToPolygonFeature,
} from '../../utils/sarAoiGeometry';

const { RangePicker } = DatePicker;

/** Days between baseline end and crisis start in default presets (crisis must start after baseline). */
const SAR_WIZARD_GAP_AFTER_BASELINE_DAYS = 14;
const SAR_WIZARD_BASELINE_LENGTH_DAYS = 30;
const SAR_WIZARD_CRISIS_LENGTH_DAYS = 7;
/** Giới hạn độ dài mỗi khoảng (ngày) — tránh dải quá dài khó chọn ảnh / mơ hồ nghiệp vụ. */
const SAR_WIZARD_MAX_RANGE_SPAN_DAYS = 120;

function disabledDateSarWindow(current: Dayjs, info: { from?: Dayjs }): boolean {
  const from = info.from;
  if (!from) return false;
  if (current.isBefore(from, 'day')) return true;
  return current.diff(from, 'day') > SAR_WIZARD_MAX_RANGE_SPAN_DAYS;
}

export interface SarFloodAnalysisWizardModalProps {
  open: boolean;
  onClose: () => void;
  /** Open ring [lng, lat][] — at least 3 vertices */
  polygonRing: [number, number][] | null;
  onSubmitted: () => void;
}

interface WizardFormValues {
  name?: string;
  notes?: string;
  baselineRange?: [Dayjs, Dayjs];
  crisisRange?: [Dayjs, Dayjs];
  observationRange?: [Dayjs, Dayjs];
  polarization?: string;
  threshold?: number;
}

export function SarFloodAnalysisWizardModal({
  open,
  onClose,
  polygonRing,
  onSubmitted,
}: SarFloodAnalysisWizardModalProps) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { addJob, updateJob, setActiveSarJobId, replaceJobId } = useFloodJobs();
  const [postSarAnalyze] = usePostGeoSarAnalyzeMutation();
  const currentUser = useAppSelector((s) => s.auth.user);
  const [form] = Form.useForm<WizardFormValues>();
  const [step, setStep] = useState(0);
  const [analysisKind, setAnalysisKind] = useState<SarAoiAnalysisKind>('sarFlood');
  const [checking, setChecking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [prevOpenKey, setPrevOpenKey] = useState('');

  const currentOpenKey = open ? `open_${polygonRing?.length ?? 0}` : 'closed';
  if (currentOpenKey !== prevOpenKey) {
    setPrevOpenKey(currentOpenKey);
    if (!open) {
      setStep(0);
      setAnalysisKind('sarFlood');
      setChecking(false);
      setSubmitting(false);
    } else if (polygonRing && polygonRing.length >= 3) {
      // Logic from old useEffect moved here
      form.resetFields();
      const crisisEnd = dayjs().year(2025);
      const crisisStart = crisisEnd.subtract(SAR_WIZARD_CRISIS_LENGTH_DAYS, 'day');
      const baselineEnd = crisisStart.subtract(1 + SAR_WIZARD_GAP_AFTER_BASELINE_DAYS, 'day');
      const baselineStart = baselineEnd.subtract(SAR_WIZARD_BASELINE_LENGTH_DAYS - 1, 'day');
      const obsEnd = dayjs().year(2025);
      const obsStart = obsEnd.subtract(SAR_WIZARD_BASELINE_LENGTH_DAYS - 1, 'day');
      form.setFieldsValue({
        polarization: 'VV',
        threshold: -16,
        baselineRange: [baselineStart, baselineEnd],
        crisisRange: [crisisStart, crisisEnd],
        observationRange: [obsStart, obsEnd],
      });
    }
  }

  const areaKm2 = useMemo(() => {
    if (!polygonRing || polygonRing.length < 3) return null;
    return computeAoiAreaKm2(polygonRing);
  }, [polygonRing]);

  const maxKm2 = useMemo(() => getSarAoiMaxAreaKm2(), []);

  const aoiFeature = useMemo(() => {
    if (!polygonRing || polygonRing.length < 3) return null;
    return openLngLatRingToPolygonFeature(polygonRing);
  }, [polygonRing]);

  // Effect for auto-close if ring is invalid
  useEffect(() => {
    if (open && (!polygonRing || polygonRing.length < 3)) {
      onClose();
    }
  }, [open, polygonRing, onClose]);

  const handleFinalSubmit = async () => {
    if (!polygonRing || polygonRing.length < 3 || !aoiFeature) return;
    const v = form.getFieldsValue();
    setSubmitting(true);
    try {
      let parameters: FloodJob['parameters'];
      let steps: FloodJob['steps'];
      let defaultName: string;

      if (analysisKind === 'sarFlood') {
        const baselineRange = v.baselineRange as [Dayjs, Dayjs] | undefined;
        const crisisRange = v.crisisRange as [Dayjs, Dayjs] | undefined;
        if (!baselineRange?.[0] || !baselineRange?.[1] || !crisisRange?.[0] || !crisisRange?.[1]) {
          message.warning(t('map.sarAoi.finalSubmitMissingParams'));
          setStep(1);
          return;
        }
        defaultName = t('map.sarAoi.defaultJobName');
        parameters = {
          analysisKind: 'sarFlood',
          baseline: baselineRange[0].format('YYYY-MM-DD'),
          baselineEnd: baselineRange[1].format('YYYY-MM-DD'),
          crisis: crisisRange[0].format('YYYY-MM-DD'),
          crisisEnd: crisisRange[1].format('YYYY-MM-DD'),
          polarization: v.polarization ?? 'VV',
          threshold: v.threshold ?? -16,
        };
        steps = [
          { name: t('ops.jobs.sarStepCollect'), status: 'pending' },
          { name: t('ops.jobs.sarStepPreprocess'), status: 'pending' },
          { name: t('ops.jobs.sarStepWater'), status: 'pending' },
          { name: t('ops.jobs.sarStepArea'), status: 'pending' },
          { name: t('ops.jobs.sarStepExport'), status: 'pending' },
        ];
      } else {
        const observationRange = v.observationRange as [Dayjs, Dayjs] | undefined;
        if (!observationRange?.[0] || !observationRange?.[1]) {
          message.warning(t('map.sarAoi.finalSubmitMissingParams'));
          setStep(1);
          return;
        }
        const o0 = observationRange[0].format('YYYY-MM-DD');
        const o1 = observationRange[1].format('YYYY-MM-DD');
        defaultName = t('map.sarAoi.defaultJobNameLandCover');
        parameters = {
          analysisKind: 'landCover',
          baseline: o0,
          baselineEnd: o1,
          crisis: o0,
          crisisEnd: o1,
          polarization: 'S2',
          threshold: 0,
        };
        steps = [
          { name: t('map.sarAoi.lcStepCollect'), status: 'pending' },
          { name: t('map.sarAoi.lcStepAtmos'), status: 'pending' },
          { name: t('map.sarAoi.lcStepClassify'), status: 'pending' },
          { name: t('map.sarAoi.lcStepZonal'), status: 'pending' },
          { name: t('map.sarAoi.lcStepExport'), status: 'pending' },
        ];
      }

      // 1. Create a placeholder job with 'running' status
      const placeholderId =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `sar-${Date.now()}`;
      const placeholderJob: FloodJob = {
        id: placeholderId,
        name: v.name?.trim() || defaultName,
        status: 'running',
        progress: 0,
        aoi: t('map.sarAoi.aoiHandDrawn', { area: (areaKm2 ?? 0).toFixed(2) }),
        aoiPolygon: aoiFeature.geometry,
        aoiAreaKm2: areaKm2 ?? undefined,
        startedAt: dayjs().toISOString(),
        submittedBy: currentUser?.name?.trim() || '—',
        parameters,
        runtime: {
          targetDurationMs: DEFAULT_TARGET_DURATION_MS[analysisKind],
          stageWeights: DEFAULT_STAGE_WEIGHTS[analysisKind],
        },
        steps,
      };

      // 2. Add job & set active so progress overlay shows
      addJob(placeholderJob);
      setActiveSarJobId(placeholderId);

      // 3. Close modal immediately
      message.success(t('map.sarAoi.jobQueued', { id: placeholderId }));
      onClose();
      onSubmitted();

      // 4. Fire API in background — update job on response
      const bRange = (v.baselineRange || v.observationRange) as [Dayjs, Dayjs];
      const cRange = (v.crisisRange || v.observationRange) as [Dayjs, Dayjs];

      postSarAnalyze({
        job_name: v.name?.trim() || defaultName,
        submitted_by: currentUser?.name?.trim() || '—',
        task_type: analysisKind === 'landCover' ? 'LAND_COVER_ANALYSIS' : 'SAR_FLOOD_MAPPING',
        parameters: {
          satellite: 'COPERNICUS/S1_GRD',
          polarization: String(v.polarization ?? 'VV'),
          water_threshold_db: Number(v.threshold ?? (analysisKind === 'landCover' ? -16 : -16)),
          apply_speckle_filter: true,
        },
        time_window: {
          baseline_start: bRange[0].startOf('day').toISOString(),
          baseline_end: bRange[1].endOf('day').toISOString(),
          crisis_start: cRange[0].startOf('day').toISOString(),
          crisis_end: cRange[1].endOf('day').toISOString(),
        },
        aoi: {
          type: 'FeatureCollection',
          features: [aoiFeature],
        },
      })
        .unwrap()
        .then((raw) => {
          const mappedJob = mapSarAnalyzeResponseToFloodJob({
            api: raw,
            name: v.name?.trim() || defaultName,
            aoiLabel: t('map.sarAoi.aoiHandDrawn', { area: (areaKm2 ?? 0).toFixed(2) }),
            aoiPolygon: aoiFeature.geometry,
            aoiAreaKm2: areaKm2 ?? undefined,
            parameters,
            submittedBy: currentUser?.name?.trim() || '—',
            analysisKind,
            stepLabels: steps.map((s) => s.name),
          });

          // Nếu BE trả về xong luôn (case xử lý cực nhanh)
          if (mappedJob.status === 'completed') {
            replaceJobId(placeholderId, mappedJob.id, mappedJob);
          } else {
            // Case bình thường (queued/running)
            if (mappedJob.id && mappedJob.id !== placeholderId) {
              replaceJobId(placeholderId, mappedJob.id, mappedJob);
            } else {
              updateJob(placeholderId, mappedJob);
            }
          }
        })
        .catch((err) => {
          updateJob(placeholderId, {
            status: 'failed',
            errorMessage: formatUserFacingApiError(err, t),
            steps: steps.map((s) => ({ ...s, status: 'error' as const })),
          });
        });
    } finally {
      setSubmitting(false);
    }
  };

  const handleStartAnalysis = async () => {
    setChecking(true);
    try {
      if (analysisKind === 'sarFlood') {
        await form.validateFields([
          'baselineRange',
          'crisisRange',
          'polarization',
          'threshold',
          'name',
        ]);
      } else {
        await form.validateFields(['observationRange', 'name']);
      }
      await handleFinalSubmit();
    } finally {
      setChecking(false);
    }
  };

  if (!open || !polygonRing || polygonRing.length < 3) return null;

  return (
    <Modal
      title={
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-primary)' }}
        >
          <Database size={18} color="#eab308" />
          <span>{t('map.sarAoi.wizardTitleGeneric')}</span>
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={640}
      styles={{
        body: { background: 'var(--mdm-surface)', padding: 16, color: 'var(--text-primary)' },
        header: {
          background: 'transparent',
          borderBottom: '1px solid var(--glass-border)',
          paddingBottom: 16,
          marginBottom: 12,
        },
      }}
      modalRender={(node) => (
        <div
          style={{
            background: 'var(--mdm-surface)',
            border: '1px solid var(--glass-border)',
            borderRadius: 12,
            overflow: 'hidden',
          }}
        >
          {node}
        </div>
      )}
    >
      <Steps
        current={step}
        size="small"
        items={[{ title: t('map.sarAoi.stepAoi') }, { title: t('map.sarAoi.stepParams') }]}
        style={{ marginBottom: 20 }}
      />

      <Form
        form={form}
        layout="vertical"
        onFinish={() => {
          if (step === 1) void handleStartAnalysis();
        }}
        style={{ color: 'var(--text-primary)' }}
      >
        {step === 0 && (
          <>
            <div style={{ marginBottom: 16, fontSize: 13, color: '#94a3b8' }}>
              {t('map.sarAoi.aoiSummary', {
                area: (areaKm2 ?? 0).toFixed(2),
                max: maxKm2,
              })}
            </div>
            <div style={{ marginBottom: 14 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  color: 'var(--text-secondary)',
                  marginBottom: 8,
                }}
              >
                {t('map.sarAoi.analysisKindSectionLabel')}
              </div>
              <Segmented<SarAoiAnalysisKind>
                block
                size="middle"
                value={analysisKind}
                onChange={(k) => setAnalysisKind(k)}
                className="[&_.ant-segmented-item-label]:!text-[12px] [&_.ant-segmented-item]:!min-h-9"
                options={(['sarFlood', 'landCover'] as const).map((k) => {
                  const meta = getAnalysisKindMeta(k);
                  const Icon = meta.Icon;
                  return {
                    value: k,
                    label: (
                      <span className="flex items-center justify-center gap-1.5">
                        <Icon size={14} className="shrink-0 opacity-90" aria-hidden />
                        {t(meta.labelKey)}
                      </span>
                    ),
                  };
                })}
              />
              <p
                style={{
                  margin: '10px 0 0',
                  fontSize: 12,
                  lineHeight: 1.45,
                  color: 'var(--text-muted)',
                }}
              >
                {analysisKind === 'sarFlood'
                  ? t('map.sarAoi.analysisKindHintFlood')
                  : t('map.sarAoi.analysisKindHintLandCover')}
              </p>
            </div>
            <Space>
              <Button type="primary" onClick={() => setStep(1)}>
                {t('map.sarAoi.next')}
              </Button>
              <Button onClick={onClose}>{t('map.sarAoi.cancel')}</Button>
            </Space>
          </>
        )}

        {step === 1 && (
          <div>
            <Form.Item
              name="name"
              preserve
              label={
                <span style={{ color: 'var(--text-secondary)' }}>{t('ops.jobs.formJobName')}</span>
              }
            >
              <Input
                placeholder={t('ops.jobs.formJobNamePh')}
                style={{
                  background: 'var(--form-field-bg)',
                  border: '1px solid var(--form-field-border)',
                  color: 'var(--text-primary)',
                }}
              />
            </Form.Item>
            {analysisKind === 'sarFlood' ? (
              <>
                <Form.Item
                  name="baselineRange"
                  preserve
                  label={
                    <Tooltip
                      title={t('map.sarAoi.baselineRangeTooltip', {
                        maxDays: SAR_WIZARD_MAX_RANGE_SPAN_DAYS,
                      })}
                    >
                      <span
                        style={{
                          color: 'var(--text-secondary)',
                          cursor: 'help',
                          borderBottom: '1px dotted var(--text-muted)',
                        }}
                      >
                        {t('map.sarAoi.baselineRange')}
                      </span>
                    </Tooltip>
                  }
                  rules={[
                    { required: true, message: t('ops.jobs.formDateRequired') },
                    {
                      validator: async (_, value: [Dayjs, Dayjs] | null) => {
                        if (!value?.[0] || !value?.[1]) return;
                        if (value[1].diff(value[0], 'day') > SAR_WIZARD_MAX_RANGE_SPAN_DAYS) {
                          throw new Error(
                            t('map.sarAoi.dateRangeExceedsMaxDays', {
                              max: SAR_WIZARD_MAX_RANGE_SPAN_DAYS,
                            }),
                          );
                        }
                      },
                    },
                    {
                      validator: async (_, value: [Dayjs, Dayjs] | null) => {
                        if (!value?.[0] || !value?.[1]) return;
                        if (value[0].year() >= 2026 || value[1].year() >= 2026) {
                          throw new Error(t('map.sarAoi.noData2026'));
                        }
                      },
                    },
                  ]}
                >
                  <RangePicker style={{ width: '100%' }} disabledDate={disabledDateSarWindow} />
                </Form.Item>
                <Form.Item
                  name="crisisRange"
                  preserve
                  label={
                    <Tooltip
                      title={t('map.sarAoi.crisisRangeTooltip', {
                        maxDays: SAR_WIZARD_MAX_RANGE_SPAN_DAYS,
                      })}
                    >
                      <span
                        style={{
                          color: 'var(--text-secondary)',
                          cursor: 'help',
                          borderBottom: '1px dotted var(--text-muted)',
                        }}
                      >
                        {t('map.sarAoi.crisisRange')}
                      </span>
                    </Tooltip>
                  }
                  rules={[
                    { required: true, message: t('ops.jobs.formDateRequired') },
                    {
                      validator: async (_, value: [Dayjs, Dayjs] | null) => {
                        if (!value?.[0] || !value?.[1]) return;
                        if (value[1].diff(value[0], 'day') > SAR_WIZARD_MAX_RANGE_SPAN_DAYS) {
                          throw new Error(
                            t('map.sarAoi.dateRangeExceedsMaxDays', {
                              max: SAR_WIZARD_MAX_RANGE_SPAN_DAYS,
                            }),
                          );
                        }
                      },
                    },
                    {
                      validator: async (_, value: [Dayjs, Dayjs] | null) => {
                        if (!value?.[0] || !value?.[1]) return;
                        if (value[0].year() >= 2026 || value[1].year() >= 2026) {
                          throw new Error(t('map.sarAoi.noData2026'));
                        }
                      },
                    },
                    {
                      validator: async (_, value: [Dayjs, Dayjs]) => {
                        const base = form.getFieldValue('baselineRange') as
                          | [Dayjs, Dayjs]
                          | undefined;
                        if (!value || !base) return;
                        if (!value[0].isAfter(base[1], 'day')) {
                          throw new Error(t('map.sarAoi.crisisMustFollowBaseline'));
                        }
                      },
                    },
                  ]}
                >
                  <RangePicker style={{ width: '100%' }} disabledDate={disabledDateSarWindow} />
                </Form.Item>
                <div style={{ display: 'flex', gap: 16 }}>
                  <Form.Item
                    name="polarization"
                    preserve
                    label={
                      <span style={{ color: 'var(--text-secondary)' }}>
                        {t('ops.jobs.polarization')}
                      </span>
                    }
                    style={{ flex: 1 }}
                  >
                    <Select>
                      <Select.Option value="VV">VV</Select.Option>
                      <Select.Option value="VH">VH</Select.Option>
                      <Select.Option value="VV+VH">
                        {t('ops.jobs.polarizationVvVhLong')}
                      </Select.Option>
                    </Select>
                  </Form.Item>
                  <Form.Item
                    name="threshold"
                    preserve
                    label={
                      <span style={{ color: 'var(--text-secondary)' }}>
                        {t('ops.jobs.sarNoiseThreshold')}
                      </span>
                    }
                    style={{ flex: 1 }}
                  >
                    <InputNumber min={-30} max={-5} step={0.5} style={{ width: '100%' }} />
                  </Form.Item>
                </div>
              </>
            ) : (
              <Form.Item
                name="observationRange"
                preserve
                label={
                  <span style={{ color: 'var(--text-secondary)' }}>
                    {t('map.sarAoi.observationRange')}
                  </span>
                }
                rules={[
                  { required: true, message: t('ops.jobs.formDateRequired') },
                  {
                    validator: async (_, value: [Dayjs, Dayjs] | null) => {
                      if (!value?.[0] || !value?.[1]) return;
                      if (value[0].year() >= 2026 || value[1].year() >= 2026) {
                        throw new Error(t('map.sarAoi.noData2026'));
                      }
                    },
                  },
                ]}
              >
                <RangePicker style={{ width: '100%' }} />
              </Form.Item>
            )}
            <Form.Item
              name="notes"
              preserve
              label={
                <span style={{ color: 'var(--text-secondary)' }}>{t('map.sarAoi.notes')}</span>
              }
            >
              <Input.TextArea
                rows={2}
                style={{
                  background: 'var(--form-field-bg)',
                  border: '1px solid var(--form-field-border)',
                  color: 'var(--text-primary)',
                }}
              />
            </Form.Item>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
              <Button onClick={() => setStep(0)}>{t('map.sarAoi.back')}</Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={checking || submitting}
                icon={<PlayCircle size={14} />}
              >
                {t('ops.jobs.startAnalysis')}
              </Button>
            </div>
          </div>
        )}
      </Form>
    </Modal>
  );
}
